import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowRight, CalendarCheck, Stethoscope, Activity, PieChart, Clock, TrendingUp, LayoutList } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import ProgressBar from './ui/ProgressBar';
import DataTable from './ui/DataTable';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const STATUS_CATEGORIES = [
    { value: 'pending', label: 'Pending', color: 'bg-amber-500' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-500' },
    { value: 'completed', label: 'Completed', color: 'bg-emerald-500' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-rose-500' }
];

const STATUS_BADGE_COLORS = {
    pending: 'yellow',
    confirmed: 'blue',
    completed: 'green',
    cancelled: 'red'
};

const APPOINTMENT_COLUMNS = [
    { key: 'petName', header: 'Pet' },
    { key: 'ownerName', header: 'Owner' },
    { key: 'clinicName', header: 'Clinic' },
    {
        key: 'date',
        header: 'Date',
        width: '140px',
        render: (row) => (row.date ? new Date(row.date).toLocaleDateString() : 'N/A')
    },
    {
        key: 'status',
        header: 'Status',
        width: '120px',
        render: (row) => {
            const color = STATUS_BADGE_COLORS[row.status?.toLowerCase()] || 'gray';
            return (
                <span className={`status-badge ${color}`}>
                    {row.status || 'Unknown'}
                </span>
            );
        }
    }
];

const MedicalTab = ({ filters, onViewDetailed }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadMedicalStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getMedicalStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load medical stats:', err);
            setError(err.message || 'Failed to load medical statistics');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadMedicalStats();
    }, [loadMedicalStats]);

    const statusData = useMemo(() => {
        if (!stats?.appointmentsByStatus) return [];
        return Object.entries(stats.appointmentsByStatus).map(([key, value]) => ({
            name: STATUS_CATEGORIES.find((c) => c.value === key.toLowerCase())?.label || key,
            value
        }));
    }, [stats?.appointmentsByStatus]);

    const graphData = stats?.graph_data || [];

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadMedicalStats} className="retry-button">Retry</button>
            </div>
        );
    }

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    Detailed <ArrowRight size={14} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="sd-grid sd-grid--kpi-primary">
                <StatCard
                    icon={CalendarCheck}
                    label="Appointments"
                    value={stats?.totalAppointments || 0}
                    trend={stats?.appointmentGrowth}
                    trendLabel=""
                    color="bg-rose-500"
                    loading={loading}
                />
                <StatCard
                    icon={Stethoscope}
                    label="Clinics"
                    value={stats?.activeClinics || 0}
                    subtitle="Registered"
                    color="bg-teal-500"
                    loading={loading}
                />
                <StatCard
                    icon={Activity}
                    label="Completion"
                    value={`${(stats?.completionRate || 0).toFixed(1)}%`}
                    subtitle="Completed"
                    color="bg-amber-500"
                    loading={loading}
                />
                <StatCard
                    icon={Clock}
                    label="Outpatient"
                    value={stats?.outpatientVisits || 0}
                    color="bg-blue-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts">
                    <SectionCard title="Appointment Status" icon={PieChart}>
                        <CustomDonutChart
                            data={statusData}
                            totalValue={stats?.totalAppointments || 0}
                            title=""
                        />
                    </SectionCard>
                    <SectionCard title="Appointments Over Time" icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={graphData} color="#ef4444" title="" />
                    </SectionCard>
                </div>
            )}

            {/* Status Breakdown + Visit Types */}
            {!loading && (
                <div className="sd-grid sd-grid--split">
                    <SectionCard title="Status Breakdown" icon={LayoutList}>
                        <div className="sd-progress-bar-list">
                            {stats?.appointmentsByStatus && Object.entries(stats.appointmentsByStatus).map(([status, count]) => {
                                const category = STATUS_CATEGORIES.find((c) => c.value === status.toLowerCase());
                                return (
                                    <ProgressBar
                                        key={status}
                                        label={category?.label || status}
                                        count={count}
                                        total={stats?.totalAppointments || 1}
                                        color={category?.color}
                                    />
                                );
                            })}
                        </div>
                    </SectionCard>

                    <SectionCard title="Visit Types" icon={Clock}>
                        <div>
                            <div className="sd-visit-card sd-visit-card--outpatient">
                                <div>
                                    <p className="sd-visit-card__title">Outpatient Visits</p>
                                    <p className="sd-visit-card__subtitle">Regular consultations</p>
                                </div>
                                <p className="sd-visit-card__value sd-visit-card__value--outpatient">
                                    {(stats?.outpatientVisits || 0).toLocaleString()}
                                </p>
                            </div>
                            <div className="sd-visit-card sd-visit-card--inpatient">
                                <div>
                                    <p className="sd-visit-card__title">Inpatient Visits</p>
                                    <p className="sd-visit-card__subtitle">Hospital stays</p>
                                </div>
                                <p className="sd-visit-card__value sd-visit-card__value--inpatient">
                                    {(stats?.inpatientVisits || 0).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* Recent Appointments */}
            {!loading && (
                <SectionCard title="Recent Appointments" icon={CalendarCheck}>
                    <DataTable
                        columns={APPOINTMENT_COLUMNS}
                        data={stats?.recentAppointments || []}
                        emptyText="No recent appointments"
                    />
                </SectionCard>
            )}
        </div>
    );
};

export default MedicalTab;
